import { ComponentProps, type MouseEventHandler, type ReactNode } from 'react';
import { twJoin } from 'tailwind-merge';

import { Image } from '@/core/Image/Image';
import { Link } from '@/core/routing/components/Link';
import { Tag } from '@/core/tamagoshiTailwind/components/Tag/Tag';
import { Span } from '@/core/tamagoshiTailwind/components/Typography/Typography';
import { ChevronRightIcon } from '@/core/tamagoshiTailwind/icons/ChevronRightIcon';
import { WarningFullIcon } from '@/core/tamagoshiTailwind/icons/WarningFullIcon';
import { TamagoshiIconComponent } from '@/core/tamagoshiTailwind/interfaces';
import { twMerge } from '@/core/tamagoshiTailwind/util/twMerge';
import { PrefixObjectKeys } from '@/core/tamagoshiTailwind/util/utils.type';

import { RestrictedReactNodeOrElement } from './components/RestrictedReactNodeOrElement';

type SectionButtonCommonProps = {
  /** Force the hover styles */
  active?: boolean;
  /** Whether the component should use no padding in larger viewports */
  compact?: boolean;
  /** Optional right-aligned content */
  endAddon?: ReactNode;
  /** Whether the chevron indicator is hidden */
  hiddenChevron?: boolean;
  /** Will disable the hover effect */
  hoverDisabled?: boolean;
  /** The icon displayed on the left side of the title */
  icon?: TamagoshiIconComponent;
  /** The title of the section button */
  title: string;
  /** Display extra information on the right side of the title */
  titleAddon?: ReactNode;
  /** The variant of the section button. Govern the title size */
  variant?: 'M' | 'L';
  /** Will display a warning badge on the right */
  warned?: boolean;
};

type ImageProps = PrefixObjectKeys<
  'image',
  Pick<ComponentProps<typeof Image>, 'alt' | 'src'>
>;

type SectionButtonProps = SectionButtonCommonProps &
  (
    | { description: string; tagText?: never }
    | { description?: never; tagText: string }
    | { description?: never; tagText?: never }
  ) &
  (
    | { icon: TamagoshiIconComponent; imageAlt?: never; imageSrc?: never }
    | ({ icon?: never } & ImageProps)
    | { icon?: never; imageAlt?: never; imageSrc?: never }
  ) &
  (
    | { href: string; onClick?: MouseEventHandler<HTMLAnchorElement> }
    | { href?: never; onClick: MouseEventHandler<HTMLButtonElement> }
  );

/**
 * A navigation button typically used in a vertical layout.
 *
 * The component includes the separator already. Render a link or a button based
 * on the `href` property.
 * When there is an `href`, the chevron will be hidden on desktop
 *
 * See the related [Figma][figma] for more details.
 *
 * [figma]: https://www.figma.com/design/KAiRlBccr6bFYmKby9wQrR?node-id=15903-25270
 */
export const SectionButton = ({
  active = false,
  compact,
  description,
  endAddon,
  hiddenChevron = false,
  hoverDisabled,
  href,
  icon: Icon,
  imageAlt,
  imageSrc,
  onClick,
  tagText,
  title,
  titleAddon,
  variant = 'M',
  warned,
}: SectionButtonProps) => (
  <ButtonOrLink
    className={twMerge(
      `flex min-h-[64px] w-full flex-1 flex-col items-start justify-center
      gap-xs border-b border-foundation-primary bg-foundation-inverted p-m
      text-foundation-primary`,
      compact && 'desktop:px-0',
      !hoverDisabled && 'hover:bg-brand-secondary',
      active && 'bg-brand-secondary',
      !tagText && !description && 'h-[64px]',
    )}
    href={href}
    onClick={onClick}
  >
    <div className="flex w-full gap-m">
      <span className="flex flex-1 items-center gap-s overflow-hidden">
        {Icon && <Icon className="h-[24px]" />}
        {imageSrc && (
          <Image
            className="size-[32px] object-cover"
            width={32}
            height={32}
            src={imageSrc}
            alt={imageAlt}
          />
        )}
        <Span
          scale={variant === 'L' ? 'body1' : 'body2'}
          weight="semibold"
          className={twJoin(
            'text-left',
            tagText || description ? 'truncate' : 'line-clamp-2',
          )}
        >
          {title}
        </Span>
        <RestrictedReactNodeOrElement
          element={titleAddon}
          className="h-[24px] flex-none"
        />
      </span>

      <span className="flex flex-initial items-center gap-xs">
        <RestrictedReactNodeOrElement element={endAddon} className="h-[24px]" />
        {warned && <WarningFullIcon className="text-warning-primary" />}
        {!hiddenChevron && (
          <ChevronRightIcon className={href ? 'desktop:hidden' : ''} />
        )}
      </span>
    </div>

    {tagText && <Tag text={tagText} variant="Information" showIcon />}

    {description && (
      <Span scale="body2" className="text-left">
        {description}
      </Span>
    )}
  </ButtonOrLink>
);

SectionButton.displayName = 'SectionButton';

function ButtonOrLink({
  children,
  className,
  href,
  onClick,
}: {
  children: ReactNode;
  className: string;
  href?: string;
  onClick?:
    | MouseEventHandler<HTMLButtonElement>
    | MouseEventHandler<HTMLAnchorElement>;
}) {
  if (href) {
    return (
      <Link
        href={href}
        className={className}
        onClick={onClick as MouseEventHandler<HTMLAnchorElement>}
      >
        {children}
      </Link>
    );
  }
  return (
    <button
      className={className}
      onClick={onClick as MouseEventHandler<HTMLButtonElement>}
    >
      {children}
    </button>
  );
}
